/**
 * Dados MOCADOS das Automacoes (AUT-01..03).
 *
 * Ainda NAO existe rota de API para automacoes: esta tela e uma casca visual
 * alimentada por este modulo. Os tipos abaixo descrevem o contrato esperado do
 * backend — quando ele existir, troca-se a origem dos dados sem mexer na tela.
 *
 * Vocabulario: em pt-BR a entidade e sempre "Item de Trabalho" / "Itens de
 * Trabalho". Nao existe Sprint no produto.
 */

/** Momento em que a regra e avaliada. */
export type AutomationTrigger = {
  /** Codigo estavel do gatilho (o backend devolveria isto). */
  code: string
  /** Frase curta que aparece no cartao do gatilho. */
  label: string
  /** Explicacao do momento exato em que a regra dispara. */
  description: string
  icon: string
}

/** Filtro que precisa ser verdadeiro para as acoes rodarem. */
export type AutomationCondition = {
  id: string
  /** Campo avaliado, ja no rotulo de UI. */
  field: string
  /** Operador em linguagem natural ("é igual a", "está em"...). */
  operator: string
  /** Valor comparado, ja formatado para leitura. */
  value: string
}

/** Efeito executado quando gatilho + condicoes batem. */
export type AutomationAction = {
  id: string
  label: string
  /** Detalhe do que sera feito (destinatario, status alvo, texto...). */
  detail: string
  icon: string
}

export type AutomationRunStatus = 'success' | 'failed' | 'skipped'

/** Uma execucao registrada no historico da regra. */
export type AutomationRun = {
  id: string
  /** ISO 8601. */
  startedAt: string
  durationMs: number
  status: AutomationRunStatus
  /** Item de Trabalho ou demanda que disparou a regra. */
  target: { code: string; title: string }
  /** Resumo do que aconteceu — sucesso, motivo do erro ou do salto. */
  message: string
}

export type AutomationRule = {
  id: string
  name: string
  description: string
  isActive: boolean
  /** Onde a regra vale (area, quadro, tipo de item...). */
  scope: string
  ownerName: string
  trigger: AutomationTrigger
  conditions: readonly AutomationCondition[]
  actions: readonly AutomationAction[]
  /** Execucoes nos ultimos 30 dias. */
  runCount30d: number
  /** Percentual de execucoes bem-sucedidas nos ultimos 30 dias (0-100). */
  successRate: number
  /** ISO 8601 ou null quando a regra nunca rodou. */
  lastRunAt: string | null
  createdAt: string
  updatedAt: string
  runs: readonly AutomationRun[]
}

/** Gatilhos disponiveis no construtor de regra. */
export const AUTOMATION_TRIGGERS: readonly AutomationTrigger[] = [
  {
    code: 'work_item.status_changed',
    label: 'Quando o status do Item de Trabalho muda',
    description: 'Dispara a cada transição de status, inclusive as feitas arrastando o cartão no quadro.',
    icon: 'fas fa-exchange-alt',
  },
  {
    code: 'work_item.created',
    label: 'Quando um Item de Trabalho é criado',
    description: 'Dispara na criação, seja pela tela, pela conversão de uma demanda ou pela API.',
    icon: 'fas fa-plus-circle',
  },
  {
    code: 'work_item.assignee_changed',
    label: 'Quando o responsável do Item de Trabalho muda',
    description: 'Dispara ao atribuir, trocar ou remover o responsável.',
    icon: 'fas fa-user-check',
  },
  {
    code: 'work_item.due_date_near',
    label: 'Quando o prazo do Item de Trabalho se aproxima',
    description: 'Avaliado uma vez por dia, às 07:00, para todos os itens ainda em aberto.',
    icon: 'fas fa-hourglass-half',
  },
  {
    code: 'demand.sla_at_risk',
    label: 'Quando o SLA da demanda entra em risco',
    description: 'Avaliado a cada 15 minutos; considera o prazo de resposta do tipo da demanda.',
    icon: 'fas fa-stopwatch',
  },
  {
    code: 'demand.created',
    label: 'Quando uma demanda é registrada',
    description: 'Dispara na entrada da demanda, antes da triagem.',
    icon: 'fas fa-inbox',
  },
  {
    code: 'schedule.weekly',
    label: 'Em um horário recorrente',
    description: 'Dispara no dia da semana e horário configurados, sem depender de evento.',
    icon: 'fas fa-calendar-alt',
  },
]

/** Campos que o construtor oferece para montar condicoes. */
export const AUTOMATION_CONDITION_FIELDS: readonly string[] = [
  'Status do Item de Trabalho',
  'Tipo de Item de Trabalho',
  'Prioridade',
  'Área responsável',
  'Responsável',
  'Empresa da demanda',
  'Prazo',
  'Quadro',
]

/** Operadores aceitos nas condicoes. */
export const AUTOMATION_CONDITION_OPERATORS: readonly string[] = [
  'é igual a',
  'é diferente de',
  'está em',
  'não está em',
  'está preenchido',
  'está vazio',
]

/** Acoes que uma regra pode executar. */
export const AUTOMATION_ACTION_KINDS: readonly { code: string; label: string; icon: string }[] = [
  { code: 'notify_assignee', label: 'Notificar o responsável', icon: 'fas fa-bell' },
  { code: 'notify_channel', label: 'Publicar em um canal', icon: 'fas fa-comments' },
  { code: 'send_email', label: 'Enviar e-mail', icon: 'fas fa-envelope' },
  { code: 'change_status', label: 'Mudar o status do Item de Trabalho', icon: 'fas fa-exchange-alt' },
  { code: 'set_assignee', label: 'Definir o responsável', icon: 'fas fa-user-plus' },
  { code: 'set_priority', label: 'Ajustar a prioridade', icon: 'fas fa-flag' },
  { code: 'create_work_item', label: 'Criar um Item de Trabalho', icon: 'fas fa-tasks' },
  { code: 'add_comment', label: 'Registrar um comentário', icon: 'fas fa-comment-dots' },
]

export const AUTOMATION_RULES: readonly AutomationRule[] = [
  {
    id: 'aut-001',
    name: 'Avisar o responsável quando o item é concluído',
    description:
      'Fecha o ciclo do Item de Trabalho: quem executou recebe a confirmação e um comentário automático registra data, hora e autor da conclusão.',
    isActive: true,
    scope: 'Todas as áreas',
    ownerName: 'Cristian Ramos',
    trigger: {
      code: 'work_item.status_changed',
      label: 'Quando o status do Item de Trabalho muda para Concluído',
      description: 'Dispara na transição de qualquer status para Concluído, inclusive pelo arrasto no quadro.',
      icon: 'fas fa-exchange-alt',
    },
    conditions: [
      { id: 'c1', field: 'Status do Item de Trabalho', operator: 'é igual a', value: 'Concluído' },
      { id: 'c2', field: 'Responsável', operator: 'está preenchido', value: '—' },
    ],
    actions: [
      {
        id: 'a1',
        label: 'Notificar o responsável',
        detail: 'Mensagem: "O item {código} foi concluído por {autor}."',
        icon: 'fas fa-bell',
      },
      {
        id: 'a2',
        label: 'Registrar um comentário',
        detail: 'Comentário automático com data, hora e autor da conclusão.',
        icon: 'fas fa-comment-dots',
      },
    ],
    runCount30d: 214,
    successRate: 99,
    lastRunAt: '2026-08-08T11:42:00-03:00',
    createdAt: '2026-04-12T09:20:00-03:00',
    updatedAt: '2026-07-30T16:05:00-03:00',
    runs: [
      {
        id: 'r-001',
        startedAt: '2026-08-08T11:42:00-03:00',
        durationMs: 320,
        status: 'success',
        target: { code: 'WI-1842', title: 'Ajustar cálculo de SLA por tipo de demanda' },
        message: 'Notificação entregue a Marina Fontes e comentário registrado.',
      },
      {
        id: 'r-002',
        startedAt: '2026-08-08T09:15:00-03:00',
        durationMs: 285,
        status: 'success',
        target: { code: 'WI-1839', title: 'Revisar textos da tela de triagem' },
        message: 'Notificação entregue a Paulo Deodato e comentário registrado.',
      },
      {
        id: 'r-003',
        startedAt: '2026-08-07T18:03:00-03:00',
        durationMs: 190,
        status: 'skipped',
        target: { code: 'WI-1830', title: 'Migrar catálogo de prioridades' },
        message: 'Condição "Responsável está preenchido" não foi atendida — item sem responsável.',
      },
      {
        id: 'r-004',
        startedAt: '2026-08-07T14:27:00-03:00',
        durationMs: 411,
        status: 'success',
        target: { code: 'WI-1826', title: 'Publicar bundle de tradução pt-BR' },
        message: 'Notificação entregue a Renata Alencar e comentário registrado.',
      },
      {
        id: 'r-005',
        startedAt: '2026-08-06T10:58:00-03:00',
        durationMs: 1290,
        status: 'failed',
        target: { code: 'WI-1811', title: 'Configurar quadro de sustentação' },
        message: 'Falha ao enviar a notificação: o membro não tem endereço de e-mail válido.',
      },
    ],
  },
  {
    id: 'aut-002',
    name: 'Escalar demanda com SLA em risco',
    description:
      'Quando faltam menos de 4 horas para o prazo de resposta, a demanda sobe de prioridade e o plantão da área é avisado no canal.',
    isActive: true,
    scope: 'Área: Atendimento ao Empresa',
    ownerName: 'Marina Fontes',
    trigger: {
      code: 'demand.sla_at_risk',
      label: 'Quando o SLA da demanda entra em risco',
      description: 'Avaliado a cada 15 minutos; dispara quando restam menos de 4 horas do prazo de resposta.',
      icon: 'fas fa-stopwatch',
    },
    conditions: [
      { id: 'c1', field: 'Empresa da demanda', operator: 'está preenchido', value: '—' },
      { id: 'c2', field: 'Prioridade', operator: 'não está em', value: 'Crítica' },
      { id: 'c3', field: 'Área responsável', operator: 'é igual a', value: 'Atendimento ao Empresa' },
    ],
    actions: [
      {
        id: 'a1',
        label: 'Ajustar a prioridade',
        detail: 'Elevar a prioridade da demanda para Crítica.',
        icon: 'fas fa-flag',
      },
      {
        id: 'a2',
        label: 'Publicar em um canal',
        detail: 'Canal #plantao-atendimento com o código da demanda e o tempo restante.',
        icon: 'fas fa-comments',
      },
    ],
    runCount30d: 63,
    successRate: 95,
    lastRunAt: '2026-08-08T08:30:00-03:00',
    createdAt: '2026-05-02T14:10:00-03:00',
    updatedAt: '2026-08-01T09:44:00-03:00',
    runs: [
      {
        id: 'r-101',
        startedAt: '2026-08-08T08:30:00-03:00',
        durationMs: 640,
        status: 'success',
        target: { code: 'DEM-0472', title: 'Erro ao emitir relatório consolidado' },
        message: 'Prioridade elevada para Crítica e aviso publicado em #plantao-atendimento.',
      },
      {
        id: 'r-102',
        startedAt: '2026-08-07T16:45:00-03:00',
        durationMs: 588,
        status: 'success',
        target: { code: 'DEM-0468', title: 'Empresa sem acesso ao painel de faturamento' },
        message: 'Prioridade elevada para Crítica e aviso publicado em #plantao-atendimento.',
      },
      {
        id: 'r-103',
        startedAt: '2026-08-07T11:12:00-03:00',
        durationMs: 2410,
        status: 'failed',
        target: { code: 'DEM-0465', title: 'Integração de boletos fora do ar' },
        message: 'O canal #plantao-atendimento estava indisponível no momento do envio.',
      },
      {
        id: 'r-104',
        startedAt: '2026-08-05T13:20:00-03:00',
        durationMs: 512,
        status: 'skipped',
        target: { code: 'DEM-0459', title: 'Dúvida sobre limite de usuários' },
        message: 'A demanda já estava com prioridade Crítica.',
      },
    ],
  },
  {
    id: 'aut-003',
    name: 'Cobrar critérios de aceite antes de liberar',
    description:
      'Impede que um Item de Trabalho chegue a Concluído com critérios de aceite em aberto: o item volta para Em revisão e o responsável recebe a lista pendente.',
    isActive: true,
    scope: 'Tipos: Funcionalidade e Ação',
    ownerName: 'Paulo Deodato',
    trigger: {
      code: 'work_item.status_changed',
      label: 'Quando o status do Item de Trabalho muda para Concluído',
      description: 'Dispara antes de gravar a transição, o que permite devolver o item ao status anterior.',
      icon: 'fas fa-exchange-alt',
    },
    conditions: [
      { id: 'c1', field: 'Tipo de Item de Trabalho', operator: 'está em', value: 'Funcionalidade, Ação' },
      { id: 'c2', field: 'Status do Item de Trabalho', operator: 'é igual a', value: 'Concluído' },
    ],
    actions: [
      {
        id: 'a1',
        label: 'Mudar o status do Item de Trabalho',
        detail: 'Devolver para Em revisão quando houver critério de aceite não atendido.',
        icon: 'fas fa-exchange-alt',
      },
      {
        id: 'a2',
        label: 'Notificar o responsável',
        detail: 'Mensagem com a lista dos critérios de aceite ainda em aberto.',
        icon: 'fas fa-bell',
      },
    ],
    runCount30d: 87,
    successRate: 100,
    lastRunAt: '2026-08-07T17:22:00-03:00',
    createdAt: '2026-03-18T10:05:00-03:00',
    updatedAt: '2026-06-25T11:30:00-03:00',
    runs: [
      {
        id: 'r-201',
        startedAt: '2026-08-07T17:22:00-03:00',
        durationMs: 380,
        status: 'success',
        target: { code: 'WI-1833', title: 'Filtro salvo por usuário no explorer' },
        message: 'Item devolvido para Em revisão — 2 critérios de aceite em aberto.',
      },
      {
        id: 'r-202',
        startedAt: '2026-08-06T15:41:00-03:00',
        durationMs: 356,
        status: 'skipped',
        target: { code: 'WI-1828', title: 'Exportar quadro em CSV' },
        message: 'Todos os critérios de aceite estavam atendidos — nenhuma ação necessária.',
      },
      {
        id: 'r-203',
        startedAt: '2026-08-05T09:08:00-03:00',
        durationMs: 402,
        status: 'success',
        target: { code: 'WI-1817', title: 'Ordenação por prazo na caixa de entrada' },
        message: 'Item devolvido para Em revisão — 1 critério de aceite em aberto.',
      },
    ],
  },
  {
    id: 'aut-004',
    name: 'Distribuir demandas novas por área',
    description:
      'Toda demanda registrada sem área definida vai para a fila de triagem da área sugerida pelo tipo de serviço escolhido pelo solicitante.',
    isActive: true,
    scope: 'Todas as demandas',
    ownerName: 'Renata Alencar',
    trigger: {
      code: 'demand.created',
      label: 'Quando uma demanda é registrada',
      description: 'Dispara na entrada da demanda, antes da triagem manual.',
      icon: 'fas fa-inbox',
    },
    conditions: [{ id: 'c1', field: 'Área responsável', operator: 'está vazio', value: '—' }],
    actions: [
      {
        id: 'a1',
        label: 'Definir o responsável',
        detail: 'Atribuir ao líder da área sugerida pelo tipo de serviço.',
        icon: 'fas fa-user-plus',
      },
      {
        id: 'a2',
        label: 'Notificar o responsável',
        detail: 'Aviso na caixa de entrada com o resumo da demanda e o prazo de resposta.',
        icon: 'fas fa-bell',
      },
    ],
    runCount30d: 158,
    successRate: 97,
    lastRunAt: '2026-08-08T10:07:00-03:00',
    createdAt: '2026-02-27T08:45:00-03:00',
    updatedAt: '2026-07-14T15:12:00-03:00',
    runs: [
      {
        id: 'r-301',
        startedAt: '2026-08-08T10:07:00-03:00',
        durationMs: 244,
        status: 'success',
        target: { code: 'DEM-0475', title: 'Solicitar novo relatório de horas por área' },
        message: 'Atribuída a Renata Alencar (Operações) e notificação enviada.',
      },
      {
        id: 'r-302',
        startedAt: '2026-08-08T09:33:00-03:00',
        durationMs: 261,
        status: 'success',
        target: { code: 'DEM-0474', title: 'Ajuste no layout do boleto' },
        message: 'Atribuída a Paulo Deodato (Produto) e notificação enviada.',
      },
      {
        id: 'r-303',
        startedAt: '2026-08-07T19:50:00-03:00',
        durationMs: 233,
        status: 'skipped',
        target: { code: 'DEM-0471', title: 'Renovação de contrato anual' },
        message: 'A demanda já chegou com a área responsável preenchida.',
      },
    ],
  },
  {
    id: 'aut-005',
    name: 'Alertar sobre prazo a vencer em 2 dias',
    description:
      'Varredura diária às 07:00 que avisa o responsável sobre Itens de Trabalho em aberto cujo prazo vence dentro de dois dias úteis.',
    isActive: true,
    scope: 'Quadro: Desenvolvimento de Produto',
    ownerName: 'Cristian Ramos',
    trigger: {
      code: 'work_item.due_date_near',
      label: 'Quando o prazo do Item de Trabalho se aproxima',
      description: 'Avaliado uma vez por dia, às 07:00, para todos os itens ainda em aberto.',
      icon: 'fas fa-hourglass-half',
    },
    conditions: [
      { id: 'c1', field: 'Prazo', operator: 'está preenchido', value: '—' },
      { id: 'c2', field: 'Status do Item de Trabalho', operator: 'não está em', value: 'Concluído, Cancelado' },
      { id: 'c3', field: 'Quadro', operator: 'é igual a', value: 'Desenvolvimento de Produto' },
    ],
    actions: [
      {
        id: 'a1',
        label: 'Notificar o responsável',
        detail: 'Resumo diário com os itens que vencem em até dois dias úteis.',
        icon: 'fas fa-bell',
      },
    ],
    runCount30d: 30,
    successRate: 100,
    lastRunAt: '2026-08-08T07:00:00-03:00',
    createdAt: '2026-06-09T13:00:00-03:00',
    updatedAt: '2026-06-09T13:00:00-03:00',
    runs: [
      {
        id: 'r-401',
        startedAt: '2026-08-08T07:00:00-03:00',
        durationMs: 3120,
        status: 'success',
        target: { code: '11 itens', title: 'Varredura diária do quadro Desenvolvimento de Produto' },
        message: '11 Itens de Trabalho com prazo próximo; 7 responsáveis notificados.',
      },
      {
        id: 'r-402',
        startedAt: '2026-08-07T07:00:00-03:00',
        durationMs: 2980,
        status: 'success',
        target: { code: '9 itens', title: 'Varredura diária do quadro Desenvolvimento de Produto' },
        message: '9 Itens de Trabalho com prazo próximo; 6 responsáveis notificados.',
      },
    ],
  },
  {
    id: 'aut-006',
    name: 'Resumo semanal do portfólio por e-mail',
    description:
      'Toda segunda-feira às 08:00 envia aos donos de solução o progresso das Funcionalidades e Ações concluídas na semana anterior.',
    isActive: false,
    scope: 'Portfólio inteiro',
    ownerName: 'Marina Fontes',
    trigger: {
      code: 'schedule.weekly',
      label: 'Toda segunda-feira às 08:00',
      description: 'Gatilho por horário: não depende de evento no Item de Trabalho nem na demanda.',
      icon: 'fas fa-calendar-alt',
    },
    conditions: [],
    actions: [
      {
        id: 'a1',
        label: 'Enviar e-mail',
        detail: 'Destinatários: donos de cada Solução. Assunto: "Portfólio — semana de {data}".',
        icon: 'fas fa-envelope',
      },
    ],
    runCount30d: 0,
    successRate: 0,
    lastRunAt: '2026-06-30T08:00:00-03:00',
    createdAt: '2026-05-19T17:30:00-03:00',
    updatedAt: '2026-07-01T09:10:00-03:00',
    runs: [
      {
        id: 'r-501',
        startedAt: '2026-06-30T08:00:00-03:00',
        durationMs: 5400,
        status: 'success',
        target: { code: '4 soluções', title: 'Resumo semanal do portfólio' },
        message: 'Enviado para 4 donos de solução. A regra foi desativada em seguida para revisão do conteúdo.',
      },
    ],
  },
  {
    id: 'aut-007',
    name: 'Abrir item de revisão de segurança',
    description:
      'Rascunho: ao concluir uma Funcionalidade marcada como sensível, criar automaticamente um Item de Trabalho de revisão de segurança vinculado.',
    isActive: false,
    scope: 'Tipo: Funcionalidade',
    ownerName: 'Paulo Deodato',
    trigger: {
      code: 'work_item.status_changed',
      label: 'Quando o status do Item de Trabalho muda para Concluído',
      description: 'Dispara na transição para Concluído. Ainda em rascunho: nunca foi ativada.',
      icon: 'fas fa-exchange-alt',
    },
    conditions: [{ id: 'c1', field: 'Tipo de Item de Trabalho', operator: 'é igual a', value: 'Funcionalidade' }],
    actions: [
      {
        id: 'a1',
        label: 'Criar um Item de Trabalho',
        detail: 'Tipo Ação, título "Revisão de segurança de {título}", relacionado ao item de origem.',
        icon: 'fas fa-tasks',
      },
    ],
    runCount30d: 0,
    successRate: 0,
    lastRunAt: null,
    createdAt: '2026-08-04T16:20:00-03:00',
    updatedAt: '2026-08-04T16:20:00-03:00',
    runs: [],
  },
]

/** Busca uma regra pelo id; devolve `null` quando o id nao existe. */
export function findAutomationRule(id: string | null | undefined): AutomationRule | null {
  if (!id) return null
  return AUTOMATION_RULES.find((rule) => rule.id === id) ?? null
}
